import { useMemo, useState } from "react"
import { FileQuestion } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import { Select } from "@/components/ui/select"
import { fmtCompactRelative } from "@/lib/format"
import { cn } from "@/lib/cn"
import type { FaqEntry, FaqEntryAgent } from "@/types/faq-entry"

type SortKey = "updatedDesc" | "nameAsc" | "inactiveLast"
type TopicFilter = string | "__all__" | "__none__"

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "updatedDesc",  label: "Recently updated" },
  { value: "nameAsc",      label: "Name A->Z" },
  { value: "inactiveLast", label: "Active first" },
]

type Props = {
  agents: FaqEntryAgent[]
  selectedAgentId: number
  onAgentChange: (id: number) => void
  faqEntries: FaqEntry[]
  selectedId: number | null
  onSelect: (id: number) => void
  search: string
  onSearchChange: (v: string) => void
}

export function FaqEntryList({
  agents, selectedAgentId, onAgentChange,
  faqEntries, selectedId, onSelect,
  search, onSearchChange,
}: Props) {
  const [sort, setSort] = useState<SortKey>("updatedDesc")
  const [selectedTopic, setSelectedTopic] = useState<TopicFilter>("__all__")

  const topicCounts = useMemo(() => {
    const counts = new Map<string, number>()
    let none = 0

    faqEntries.forEach((entry) => {
      if (entry.topics.length === 0) {
        none += 1
        return
      }

      entry.topics.forEach((topic) => {
        counts.set(topic, (counts.get(topic) ?? 0) + 1)
      })
    })

    return {
      topics: [...counts.entries()]
        .map(([topic, count]) => ({ topic, count }))
        .sort((a, b) => b.count - a.count || a.topic.localeCompare(b.topic)),
      none,
    }
  }, [faqEntries])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return faqEntries.filter((entry) => {
      if (selectedTopic === "__none__" && entry.topics.length > 0) return false
      if (selectedTopic !== "__all__" && selectedTopic !== "__none__" && !entry.topics.includes(selectedTopic)) return false
      if (!q) return true

      return (
        entry.name.toLowerCase().includes(q) ||
        entry.details.toLowerCase().includes(q) ||
        entry.topics.some((topic) => topic.toLowerCase().includes(q)) ||
        entry.keywords.some((keyword) => keyword.toLowerCase().includes(q))
      )
    })
  }, [faqEntries, search, selectedTopic])

  const sorted = useMemo(() => {
    const rows = [...filtered]
    rows.sort((a, b) => {
      if (sort === "nameAsc") return a.name.localeCompare(b.name)
      if (sort === "inactiveLast") {
        if (a.active !== b.active) return a.active ? -1 : 1
        return a.name.localeCompare(b.name)
      }
      return Date.parse(b.updatedAt) - Date.parse(a.updatedAt)
    })
    return rows
  }, [filtered, sort])

  const agentOptions = useMemo(
    () => agents.map((a) => ({ value: a.id, label: a.name })),
    [agents],
  )
  const currentAgent = agentOptions.find((o) => o.value === selectedAgentId) ?? agentOptions[0]

  return (
    <Card className="flex h-full min-h-0 flex-col p-0">
      <header className="space-y-3 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <FileQuestion size={16} strokeWidth={2} className="text-app-muted" />
          <h2 className="text-base font-semibold text-app-text">FAQ catalog</h2>
        </div>

        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search FAQ entries..."
          className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text placeholder:text-app-muted focus:border-brand-red focus:outline-none"
        />

        {agents.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-app-muted">Agent</span>
            <div className="min-w-[160px]">
              <Select<number>
                value={currentAgent}
                options={agentOptions}
                onChange={(o) => o && onAgentChange(o.value)}
                isSearchable={agents.length > 6}
              />
            </div>
          </div>
        )}
      </header>

      <div className="space-y-3 border-b border-border bg-app-panel2/30 px-4 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-[11px] uppercase tracking-wide text-app-muted">Topics</span>
          <TopicChip
            label="All"
            count={faqEntries.length}
            selected={selectedTopic === "__all__"}
            onClick={() => setSelectedTopic("__all__")}
          />
          {topicCounts.topics.map(({ topic, count }) => (
            <TopicChip
              key={topic}
              label={topic}
              count={count}
              selected={selectedTopic === topic}
              onClick={() => setSelectedTopic(topic)}
            />
          ))}
          {topicCounts.none > 0 && (
            <TopicChip
              label="No topic"
              count={topicCounts.none}
              selected={selectedTopic === "__none__"}
              onClick={() => setSelectedTopic("__none__")}
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-3 text-[11px] text-app-muted">
          <div>{sorted.length} FAQ entr{sorted.length === 1 ? "y" : "ies"}</div>
          <div className="min-w-[180px]">
            <Select<SortKey>
              value={SORT_OPTIONS.find((o) => o.value === sort) ?? SORT_OPTIONS[0]}
              options={SORT_OPTIONS}
              onChange={(o) => o && setSort(o.value)}
              isSearchable={false}
            />
          </div>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6 text-sm text-app-muted">
          {faqEntries.length === 0 ? "No FAQ entries yet — click + New FAQ entry to add one." : "No matches."}
        </div>
      ) : (
        <div className="min-h-0 flex-1 lg:overflow-y-auto">
          <div className="hidden md:block">
            <ColumnHeader />
          </div>
          {sorted.map((entry) => (
            <FaqEntryRow
              key={entry.id}
              faqEntry={entry}
              selected={entry.id === selectedId}
              onClick={() => onSelect(entry.id)}
            />
          ))}
        </div>
      )}
    </Card>
  )
}

function TopicChip({ label, count, selected, onClick }: {
  label: string
  count: number
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex min-h-8 max-w-full items-center gap-1.5 rounded-control border px-2.5 py-1 text-xs transition",
        selected
          ? "border-brand-red bg-brand-redSoft text-brand-red"
          : "border-border bg-app-bg text-app-muted hover:border-brand-red/50 hover:text-app-text",
      )}
    >
      <span className="truncate">{label}</span>
      <span className={cn(
        "rounded-chip px-1.5 py-0.5 text-[10px] tabular-nums",
        selected ? "bg-brand-red/15 text-brand-red" : "bg-white/5 text-app-muted",
      )}>
        {count}
      </span>
    </button>
  )
}

const ROW_GRID =
  "md:grid md:grid-cols-[28px_minmax(0,1.8fr)_96px_92px_92px] md:items-center md:gap-3"

function ColumnHeader() {
  return (
    <div
      className={cn(
        ROW_GRID,
        "border-b border-border bg-app-panel2/20 px-4 py-2 text-[11px] uppercase tracking-wide text-app-muted",
      )}
    >
      <div />
      <div>Name</div>
      <div>Status</div>
      <div>Topics</div>
      <div className="text-right">Updated</div>
    </div>
  )
}

function FaqEntryRow({ faqEntry, selected, onClick }: {
  faqEntry: FaqEntry
  selected: boolean
  onClick: () => void
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick() } }}
      className={cn(
        ROW_GRID,
        "cursor-pointer border border-transparent border-b border-b-border/60 px-4 py-3 outline-none transition last:border-b-0",
        selected
          ? "rounded-control border-brand-red bg-brand-redSoft/20 shadow-[inset_0_0_0_1px_rgb(220_38_38)]"
          : "hover:bg-white/[0.02] focus-visible:bg-white/[0.04]",
      )}
    >
      <div className="flex items-start gap-3 md:contents">
        <div className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-brand-redSoft",
          selected ? "border-brand-red text-brand-red" : "border-brand-red/30 text-brand-red",
        )}>
          <FileQuestion size={12} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-app-text">{faqEntry.name}</div>
          <div className="mt-0.5 truncate text-xs text-app-muted">{faqEntry.details}</div>
          {faqEntry.imported && (
            <div className="mt-0.5 truncate font-mono text-[11px] text-app-muted">
              {faqEntry.supportPlatformConnectionName ?? "Imported"}{faqEntry.externalId ? ` / ${faqEntry.externalId}` : ""}
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-app-muted md:contents md:text-sm">
        <div>
          <Badge tone={faqEntry.active ? "success" : "neutral"} className="px-2 py-0.5 text-[11px]">
            {faqEntry.active ? "Active" : "Inactive"}
          </Badge>
        </div>
        <div className="md:text-app-text">
          <span className="md:hidden">Topics: </span>
          <span className="tabular-nums">{faqEntry.topics.length}</span>
        </div>
        <div className="md:text-right">
          <span className="md:hidden">Updated: </span>
          {fmtCompactRelative(faqEntry.updatedAt)}
        </div>
      </div>
    </div>
  )
}
